// Detalle/editor de un plan del segmento Estándar (mock, tab 1): rango de enganche, condiciones
// económicas, simulador y mini-historial. Los cortes SCI NO se editan aquí — van en la barra de
// segmentación (atómica entre planes).
import { useState } from 'react';
import { SaleTypeEnum } from '@fiado/type-kit/bin/loanOfferings/enums/SaleTypeEnum';
import { userMessageOf } from '@/api/errors';
import { Button, ErrorState, Loading, NumberInput } from '@/kit';
import { formatDateMx } from '@/lib/dateHelpers';
import { TierPill } from '@/shared/patterns/Pills';
import { useCreditPlanLog } from '../api/usePolicies';
import {
    decimalToPoints,
    planTierToLevel,
    pointsToDecimal,
    prepaymentMinimumError,
} from '../logic';
import { AmortizationSimulator } from './AmortizationSimulator';
import { HistoryList } from './HistoryList';
import { DiscardX, FIELD_INPUT, FIELD_LABEL, SecTitle } from './ui';
import {
    PREPAYMENT_MINIMUM_MODE_LABEL,
    SALE_TYPE_LABEL,
    TIER_LABEL,
    type CreditPlan,
    type PlanPrepaymentMinimum,
    type PrepaymentMinimumMode,
} from '../types';

// Cotas de `tnaAnnual` en `UpdateCreditPlanRequest` de loan-offerings (2.0–2.6). Sin ellas el
// input aceptaba 0–500 y el PUT rebotaba con 400 recién al publicar.
const TNA_MIN_PCT = 200;
const TNA_MAX_PCT = 260;

// Decisión de negocio: el enganche máximo queda congelado en 95% para todos los niveles, hoy y
// para planes futuros. El backend lo fuerza igual en create/update; este es el candado del front.
const DOWN_MAX_PCT_FROZEN = 95;
const DOWN_MAX_HINT_ID = 'plan-down-max-frozen-hint';

/** Fracción → porcentaje visible sin cola de float (0.16 → «16», 0.085 → «8.5»). */
function pctLabel(fraction: number): string {
    return String(Number((fraction * 100).toFixed(2)));
}

/** Tipos de venta del plan en es-MX y en orden alfabético; vacío = el plan no aplica a ninguno. */
function saleTypesLabel(saleTypes: SaleTypeEnum[]): string {
    return saleTypes
        .map((saleType) => SALE_TYPE_LABEL[saleType])
        .sort((a, b) => a.localeCompare(b, 'es-MX'))
        .join(' · ');
}

export function PlanDetail({
    plan,
    dirty,
    onChange,
    onPublish,
    onDiscard,
}: {
    plan: CreditPlan;
    dirty: boolean;
    onChange: (plan: CreditPlan) => void;
    onPublish: () => void;
    onDiscard: () => void;
}) {
    const engMin = Math.round(plan.downMinPct * 100);
    // Congelado: ya no sale de `plan.downMaxPct` — es la misma cota para los cuatro niveles.
    const engMax = DOWN_MAX_PCT_FROZEN;
    const engValid = engMin <= engMax;
    const minimumError = prepaymentMinimumError(plan.prepaymentMinimum);
    // Un plan inactivo se consulta pero no se edita: la pantalla no tiene forma de reactivarlo.
    const readOnly = !plan.active;
    const appliesToNewSale = plan.saleTypes.includes(SaleTypeEnum.NEW);
    const salesLabel = saleTypesLabel(plan.saleTypes);

    return (
        <>
            {/* .detail-head */}
            <div className="flex flex-none items-start justify-between gap-3 border-b border-border bg-card px-4 py-3">
                <div className="min-w-0 flex-1">
                    <div className="text-[15px] font-bold tracking-tight">
                        Plan {TIER_LABEL[plan.tier]}
                    </div>
                    <div className="mt-0.5 text-[10.5px] text-muted-foreground">
                        Versión {plan.version} · publicada {formatDateMx(plan.lastModified)} ·{' '}
                        {salesLabel === '' ? 'sin tipo de venta' : salesLabel}
                        {plan.active ? null : (
                            <b className="ml-1.5 font-semibold text-warning">
                                · inactivo, el wizard no lo ofrece
                            </b>
                        )}
                        {appliesToNewSale ? null : (
                            <b className="ml-1.5 font-semibold text-warning">
                                · el wizard de venta nueva no ofrece este plan
                            </b>
                        )}
                    </div>
                </div>
                <div className="flex flex-none items-center gap-1.5">
                    <TierPill level={planTierToLevel(plan.tier)} />
                    {dirty && !readOnly ? (
                        <>
                            <Button
                                size="sm"
                                className="h-6 px-2.5 text-[11px]"
                                disabled={!engValid || minimumError !== null}
                                onClick={onPublish}
                            >
                                Publicar como nueva versión
                            </Button>
                            <DiscardX onClick={onDiscard} />
                        </>
                    ) : null}
                </div>
            </div>

            {/* .detail-body */}
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-3.5 py-2.5">
                {readOnly ? (
                    <div
                        role="status"
                        className="mb-2.5 rounded-[5px] border border-warning/40 bg-warning/10 px-2.5 py-2 text-[11px] leading-snug text-foreground"
                    >
                        <b className="font-semibold">Este plan está inactivo.</b> El wizard no lo
                        ofrece y sus condiciones no se editan desde esta pantalla. Puedes consultar
                        el simulador y el historial.
                    </div>
                ) : null}

                {/* Rango de enganche */}
                <div className="mb-2.5 border-b border-border/50 pb-2.5">
                    <div className="rounded-[5px] border-l-2 border-primary bg-muted/25 px-2.5 py-2">
                        <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                            Enganche que puede pedir el cliente
                        </div>
                        <div className="grid grid-cols-[1fr_18px_1fr] items-center gap-1.5">
                            <div className="flex items-center gap-1.5">
                                <span className="flex-none text-[9px] font-bold uppercase tracking-wide text-muted-foreground">
                                    Mínimo
                                </span>
                                <NumberInput
                                    value={engMin}
                                    min={0}
                                    max={100}
                                    step={1}
                                    aria-label="Enganche mínimo (%)"
                                    disabled={readOnly}
                                    className="w-full text-center font-semibold"
                                    onValueChange={(v) => {
                                        onChange({ ...plan, downMinPct: v / 100 });
                                    }}
                                />
                                <span className="text-[10.5px] font-semibold text-muted-foreground">
                                    %
                                </span>
                            </div>
                            <div className="flex items-center justify-center text-sm font-light text-muted-foreground">
                                —
                            </div>
                            <div className="flex items-center gap-1.5">
                                <span className="flex-none text-[9px] font-bold uppercase tracking-wide text-muted-foreground">
                                    Máximo
                                </span>
                                <NumberInput
                                    value={DOWN_MAX_PCT_FROZEN}
                                    min={0}
                                    max={100}
                                    step={1}
                                    aria-label="Enganche máximo (%)"
                                    aria-describedby={DOWN_MAX_HINT_ID}
                                    disabled
                                    className="w-full text-center font-semibold"
                                    onValueChange={() => {
                                        // Congelado: el admin no lo mueve, así que no hay nada que aplicar.
                                    }}
                                />
                                <span className="text-[10.5px] font-semibold text-muted-foreground">
                                    %
                                </span>
                            </div>
                        </div>
                        {!engValid ? (
                            <div className="mt-2 flex items-center gap-1.5 rounded-[5px] bg-destructive/10 px-2.5 py-1.5 text-[11px] font-semibold text-destructive">
                                <span className="flex-none font-bold">⚠</span>
                                <span>Mínimo debe ser menor o igual al máximo</span>
                            </div>
                        ) : null}
                        <div
                            id={DOWN_MAX_HINT_ID}
                            className="mt-1.5 text-[10px] leading-snug text-muted-foreground"
                        >
                            El vendedor puede pedir enganche dentro de este rango. El mínimo es el
                            piso obligatorio para el cliente. El máximo está congelado en{' '}
                            {DOWN_MAX_PCT_FROZEN}% para todos los niveles y no se edita desde aquí.
                        </div>
                    </div>
                </div>

                {/* Condiciones económicas */}
                <div className="mb-2.5 border-b border-border/50 pb-2.5">
                    <SecTitle>Condiciones económicas del crédito</SecTitle>
                    <div className="grid grid-cols-3 gap-x-3.5 gap-y-2.5">
                        <div>
                            <label className={FIELD_LABEL} htmlFor="plan-tna">
                                Tasa anual (TNA %)
                            </label>
                            <NumberInput
                                id="plan-tna"
                                value={Math.round(plan.tna * 100)}
                                min={TNA_MIN_PCT}
                                max={TNA_MAX_PCT}
                                step={1}
                                aria-label="Tasa anual (TNA %)"
                                disabled={readOnly}
                                className="w-full px-2 py-1.5"
                                onValueChange={(v) => {
                                    onChange({ ...plan, tna: v / 100 });
                                }}
                            />
                            <div className="mt-0.5 text-[10px] text-muted-foreground">
                                Entre {TNA_MIN_PCT}% y {TNA_MAX_PCT}% · IVA{' '}
                                {pctLabel(plan.ivaOnInterest)}% sobre intereses
                            </div>
                        </div>
                        <div>
                            <label className={FIELD_LABEL} htmlFor="plan-opening-fee">
                                Comisión de apertura (%)
                            </label>
                            <NumberInput
                                id="plan-opening-fee"
                                value={Number((plan.openingFeePct * 100).toFixed(1))}
                                min={0}
                                max={20}
                                step={0.5}
                                aria-label="Comisión de apertura (%)"
                                disabled={readOnly}
                                className="w-full px-2 py-1.5"
                                onValueChange={(v) => {
                                    onChange({ ...plan, openingFeePct: v / 100 });
                                }}
                            />
                        </div>
                        <div>
                            <label className={FIELD_LABEL} htmlFor="plan-max-amount">
                                Monto máximo ($)
                            </label>
                            <NumberInput
                                id="plan-max-amount"
                                value={plan.maxAmount}
                                min={0}
                                step={500}
                                aria-label="Monto máximo ($)"
                                disabled={readOnly}
                                className="w-full px-2 py-1.5"
                                onValueChange={(v) => {
                                    onChange({ ...plan, maxAmount: v });
                                }}
                            />
                        </div>
                        <div className="col-span-3">
                            <label className={FIELD_LABEL} htmlFor="plan-terms">
                                Plazos disponibles (semanas, separados por coma)
                            </label>
                            <TermsInput
                                id="plan-terms"
                                terms={plan.termsWeeks}
                                disabled={readOnly}
                                onTermsChange={(terms) => {
                                    onChange({ ...plan, termsWeeks: terms });
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* Abono a capital */}
                <div className="mb-2.5 border-b border-border/50 pb-2.5">
                    <SecTitle>Abono a capital · mínimo</SecTitle>
                    <PrepaymentMinimumEditor
                        minimum={plan.prepaymentMinimum}
                        error={minimumError}
                        disabled={readOnly}
                        onChange={(prepaymentMinimum) => {
                            onChange({ ...plan, prepaymentMinimum });
                        }}
                    />
                </div>

                {/* Simulador */}
                <div className="mb-2.5 border-b border-border/50 pb-2.5">
                    <SecTitle>Simulador · amortización semanal</SecTitle>
                    <AmortizationSimulator
                        tna={plan.tna}
                        openingFeePct={plan.openingFeePct}
                        vatRate={plan.ivaOnInterest}
                        terms={plan.termsWeeks}
                        defaultDownPct={engMin}
                    />
                </div>

                {/* Historial — la bitácora real del plan (GET /credit-plans/{planId}/log). */}
                <div>
                    <SecTitle>Historial de este plan</SecTitle>
                    <PlanHistory planId={plan.planId} />
                </div>
            </div>
        </>
    );
}

/**
 * La bitácora del plan. Vacío y error se dicen distinto: un plan recién dado de alta no tiene
 * asientos y eso es normal, mientras que una lectura fallida sí invita a reintentar.
 */
function PlanHistory({ planId }: { planId: string }) {
    const { rows, isLoading, error, refetch } = useCreditPlanLog(planId);

    if (isLoading) return <Loading size="sm" text="Cargando historial…" />;

    if (error) {
        return (
            <ErrorState
                className="px-4 py-6"
                title="No se pudo cargar el historial"
                description={userMessageOf(error)}
                onRetry={refetch}
            />
        );
    }

    if (rows.length === 0) {
        return (
            <div className="rounded-md border border-border bg-card px-3 py-3 text-center text-[10.5px] text-muted-foreground">
                Todavía no hay cambios registrados para este plan.
            </div>
        );
    }

    return <HistoryList rows={rows} />;
}

const PREPAYMENT_MINIMUM_MODES: PrepaymentMinimumMode[] = [
    'ANY',
    'PERCENT_OF_PERIOD_PAYMENT',
    'FIXED',
];

const PREPAYMENT_MINIMUM_HINT: Record<PrepaymentMinimumMode, string> = {
    ANY: 'El cliente puede abonar a capital cualquier monto.',
    PERCENT_OF_PERIOD_PAYMENT: 'El abono mínimo es ese porcentaje del pago semanal del crédito.',
    FIXED: 'El abono mínimo es ese monto, en pesos.',
};

/** Lo tecleado como número; vacío o ilegible es «sin capturar». */
function parseTyped(text: string): number | null {
    const value = Number(text);
    return text.trim() === '' || !Number.isFinite(value) ? null : value;
}

/**
 * Modo del mínimo y, según el modo, el porcentaje (se ve 50, viaja 0.5) o el monto en pesos. Lo
 * tecleado en cada modo se conserva al cambiar de modo dentro de la misma edición.
 */
function PrepaymentMinimumEditor({
    minimum,
    error,
    disabled,
    onChange,
}: {
    minimum: PlanPrepaymentMinimum;
    error: string | null;
    disabled: boolean;
    onChange: (minimum: PlanPrepaymentMinimum) => void;
}) {
    const [pctText, setPctText] = useState(() =>
        typeof minimum.periodPaymentPct === 'number'
            ? String(decimalToPoints(minimum.periodPaymentPct))
            : '',
    );
    const [amountText, setAmountText] = useState(() =>
        typeof minimum.amount === 'number' ? String(minimum.amount) : '',
    );

    const build = (
        mode: PrepaymentMinimumMode,
        pct: string,
        amount: string,
    ): PlanPrepaymentMinimum => {
        if (mode === 'PERCENT_OF_PERIOD_PAYMENT') {
            const points = parseTyped(pct);
            return { mode, periodPaymentPct: points === null ? null : pointsToDecimal(points) };
        }
        if (mode === 'FIXED') return { mode, amount: parseTyped(amount) };
        return { mode: 'ANY' };
    };

    return (
        <div className="grid grid-cols-3 gap-x-3.5 gap-y-2">
            <div>
                <label className={FIELD_LABEL} htmlFor="plan-prepayment-mode">
                    Mínimo del abono
                </label>
                <select
                    id="plan-prepayment-mode"
                    value={minimum.mode}
                    disabled={disabled}
                    onChange={(e) => {
                        const mode = e.target.value as PrepaymentMinimumMode;
                        onChange(build(mode, pctText, amountText));
                    }}
                    className={FIELD_INPUT}
                >
                    {PREPAYMENT_MINIMUM_MODES.map((mode) => (
                        <option key={mode} value={mode}>
                            {PREPAYMENT_MINIMUM_MODE_LABEL[mode]}
                        </option>
                    ))}
                </select>
            </div>
            {minimum.mode === 'PERCENT_OF_PERIOD_PAYMENT' ? (
                <div>
                    <label className={FIELD_LABEL} htmlFor="plan-prepayment-pct">
                        % del pago semanal
                    </label>
                    <input
                        id="plan-prepayment-pct"
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={pctText}
                        disabled={disabled}
                        aria-invalid={error !== null}
                        onChange={(e) => {
                            setPctText(e.target.value);
                            onChange(build(minimum.mode, e.target.value, amountText));
                        }}
                        className={FIELD_INPUT}
                    />
                </div>
            ) : null}
            {minimum.mode === 'FIXED' ? (
                <div>
                    <label className={FIELD_LABEL} htmlFor="plan-prepayment-amount">
                        Monto mínimo ($)
                    </label>
                    <input
                        id="plan-prepayment-amount"
                        type="number"
                        min={0.01}
                        step={0.01}
                        value={amountText}
                        disabled={disabled}
                        aria-invalid={error !== null}
                        onChange={(e) => {
                            setAmountText(e.target.value);
                            onChange(build(minimum.mode, pctText, e.target.value));
                        }}
                        className={FIELD_INPUT}
                    />
                </div>
            ) : null}
            <div className="col-span-3 text-[10px] leading-snug text-muted-foreground">
                {PREPAYMENT_MINIMUM_HINT[minimum.mode]} Rige para todos los créditos del plan,
                también los ya firmados, desde que se publica.
            </div>
            {error === null ? null : (
                <div
                    role="alert"
                    className="col-span-3 flex items-center gap-1.5 rounded-[5px] bg-destructive/10 px-2.5 py-1.5 text-[11px] font-semibold text-destructive"
                >
                    <span className="flex-none font-bold">⚠</span>
                    <span>{error}</span>
                </div>
            )}
        </div>
    );
}

/** CSV de plazos con borrador local (permite teclear "8, 12," sin que el parseo pelee). */
function TermsInput({
    id,
    terms,
    disabled,
    onTermsChange,
}: {
    id: string;
    terms: number[];
    disabled?: boolean;
    onTermsChange: (terms: number[]) => void;
}) {
    const [draft, setDraft] = useState<string | null>(null);
    return (
        <input
            id={id}
            type="text"
            disabled={disabled}
            placeholder="ej. 8, 12, 16, 20"
            value={draft ?? terms.join(', ')}
            onChange={(e) => {
                const raw = e.target.value;
                setDraft(raw);
                const parsed = raw
                    .split(',')
                    .map((x) => Number(x.trim()))
                    .filter((x) => x > 0);
                onTermsChange(parsed);
            }}
            onBlur={() => {
                setDraft(null);
            }}
            className={FIELD_INPUT}
        />
    );
}
